import { trackEvent } from '@tuic/tracking';
import { RefObject, useEffect } from 'react';

/**
 * Hook that tracks when a video player enters fullscreen mode
 *
 * @param playerRef - Reference to video or iframe element
 * @param videoName - Normalized name of the video (e.g. 'jeder-moment-eine-reise-fuer-sich')
 * @param videoID - ID of the video
 *
 * @example
 * ```tsx
 * const videoRef = useRef<HTMLVideoElement>(null);
 * useFullscreenListener(videoRef, 'jeder-moment-eine-reise-fuer-sich', 'VxZJv8qTBeg');
 * ```
 */
export const useFullscreenListener = (
  playerRef:
    | RefObject<HTMLVideoElement | null>
    | RefObject<HTMLIFrameElement | null>,
  videoName: string | undefined,
  videoID: string | undefined,
) => {
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const onFullscreenChange = () => {
      if (document.fullscreenElement === player) {
        trackEvent({
          event_name: 'video-full-screen-on',
          video_name: videoName,
          video_id: videoID,
        });
      } else {
        trackEvent({
          event_name: 'video-full-screen-off',
          video_name: videoName,
          video_id: videoID,
        });
      }
    };

    player.addEventListener('fullscreenchange', onFullscreenChange);
    player.addEventListener('webkitfullscreenchange', onFullscreenChange);
    player.addEventListener('mozfullscreenchange', onFullscreenChange);
    player.addEventListener('MSFullscreenChange', onFullscreenChange);

    return () => {
      player.removeEventListener('fullscreenchange', onFullscreenChange);
      player.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      player.removeEventListener('mozfullscreenchange', onFullscreenChange);
      player.removeEventListener('MSFullscreenChange', onFullscreenChange);
    };
  }, [playerRef, videoName, videoID]);
};
